import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { payslips as api, employees as empApi, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Receipt, Plus, Eye, Trash2, ArrowLeft } from "lucide-react";

export default function Payslips() {
  const nav = useNavigate();
  const loc = useLocation();
  const [list, setList] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => Promise.all([api.list(), empApi.list()]).then(([p, e]) => { setList(p); setEmps(e); }).catch((err) => toast.error(apiError(err)));
  useEffect(() => { load(); }, []);

  const startNew = (employeeId) => {
    const emp = emps.find((x) => x.id === employeeId);
    setForm({
      employee_id: employeeId || "",
      period: new Date().toLocaleString("en-ZA", { month: "long", year: "numeric" }),
      earnings: [{ description: "Basic Salary", amount: emp ? emp.salary : 0 }],
      deductions: [],
      paye: emp ? Math.round(emp.salary * 0.18 * 100) / 100 : 0,
      uif: emp ? Math.round(Math.min(emp.salary * 0.01, 177.12) * 100) / 100 : 0,
      notes: "",
    });
    setOpen(true);
  };

  useEffect(() => {
    if (loc.state?.employeeId && emps.length) { startNew(loc.state.employeeId); window.history.replaceState({}, ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emps]);

  const pickEmp = (eid) => {
    const emp = emps.find((x) => x.id === eid);
    setForm({ ...form, employee_id: eid, earnings: [{ description: "Basic Salary", amount: emp ? emp.salary : 0 }], paye: emp ? Math.round(emp.salary * 0.18 * 100) / 100 : 0, uif: emp ? Math.round(Math.min(emp.salary * 0.01, 177.12) * 100) / 100 : 0 });
  };
  const setLine = (kind, idx, key, val) => {
    const arr = [...form[kind]]; arr[idx] = { ...arr[idx], [key]: key === "amount" ? Number(val) : val }; setForm({ ...form, [kind]: arr });
  };
  const addLine = (kind) => setForm({ ...form, [kind]: [...form[kind], { description: "", amount: 0 }] });
  const rmLine = (kind, idx) => setForm({ ...form, [kind]: form[kind].filter((_, i) => i !== idx) });

  const gross = form ? form.earnings.reduce((a, e) => a + Number(e.amount || 0), 0) : 0;
  const otherDed = form ? form.deductions.reduce((a, d) => a + Number(d.amount || 0), 0) : 0;
  const net = form ? gross - Number(form.paye) - Number(form.uif) - otherDed : 0;

  const save = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error("Select an employee.");
    setSaving(true);
    try { const p = await api.create(form); toast.success(`Payslip ${p.number} created`); setOpen(false); nav(`/payslips/${p.id}`); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.remove(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;

  return (
    <div>
      <PageHeader title="Payslips" subtitle="Generate and store employee payslips.">
        <Button onClick={() => startNew("")} disabled={emps.length === 0} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="new-payslip-button"><Plus className="w-4 h-4 mr-1.5" /> New Payslip</Button>
      </PageHeader>

      {emps.length === 0 ? (
        <EmptyState icon={Receipt} title="Add employees first" description="You need at least one employee to generate payslips."
          action={<Button onClick={() => nav("/employees")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="go-employees">Go to Employees</Button>} />
      ) : list.length === 0 ? (
        <EmptyState icon={Receipt} title="No payslips yet" description="Generate your first payslip."
          action={<Button onClick={() => startNew("")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-new-payslip"><Plus className="w-4 h-4 mr-1.5" /> New Payslip</Button>} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">Payslip</th>
                <th className="px-5 py-3 font-semibold">Employee</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Period</th>
                <th className="px-5 py-3 font-semibold text-right">Net Pay</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`payslip-row-${p.id}`}>
                  <td className="px-5 py-3.5"><button onClick={() => nav(`/payslips/${p.id}`)} className="font-semibold text-[#0B132B] hover:text-[#D4AF37]">{p.number}</button><div className="text-xs text-slate-400">{fmtDate(p.created_at)}</div></td>
                  <td className="px-5 py-3.5 text-slate-700">{p.employee?.name}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{p.period}</td>
                  <td className="px-5 py-3.5 text-right font-semibold text-emerald-600">{money(p.net)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button variant="ghost" size="icon" onClick={() => nav(`/payslips/${p.id}`)} data-testid={`view-payslip-${p.id}`}><Eye className="w-4 h-4" /></Button>
                      <ConfirmDelete onConfirm={() => remove(p.id)} title="Delete payslip?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-payslip-${p.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="payslip-dialog">
          <DialogHeader><DialogTitle>New Payslip</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Employee *</Label>
                  <Select value={form.employee_id} onValueChange={pickEmp}>
                    <SelectTrigger className="mt-1.5" data-testid="payslip-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Period</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="mt-1.5" data-testid="payslip-period-input" /></div>
              </div>

              <div>
                <div className="flex justify-between items-center"><Label>Earnings</Label><Button type="button" size="sm" variant="outline" onClick={() => addLine("earnings")} data-testid="add-earning">+ Add</Button></div>
                {form.earnings.map((it, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={it.description} onChange={(e) => setLine("earnings", idx, "description", e.target.value)} placeholder="Description" data-testid={`earning-desc-${idx}`} />
                    <Input type="number" step="0.01" value={it.amount} onChange={(e) => setLine("earnings", idx, "amount", e.target.value)} className="w-32" data-testid={`earning-amount-${idx}`} />
                    <Button type="button" variant="ghost" size="icon" className="text-rose-500" onClick={() => rmLine("earnings", idx)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>PAYE (Tax)</Label><Input type="number" step="0.01" value={form.paye} onChange={(e) => setForm({ ...form, paye: Number(e.target.value) })} className="mt-1.5" data-testid="payslip-paye-input" /></div>
                <div><Label>UIF</Label><Input type="number" step="0.01" value={form.uif} onChange={(e) => setForm({ ...form, uif: Number(e.target.value) })} className="mt-1.5" data-testid="payslip-uif-input" /></div>
              </div>

              <div>
                <div className="flex justify-between items-center"><Label>Other Deductions</Label><Button type="button" size="sm" variant="outline" onClick={() => addLine("deductions")} data-testid="add-deduction">+ Add</Button></div>
                {form.deductions.map((it, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={it.description} onChange={(e) => setLine("deductions", idx, "description", e.target.value)} placeholder="Description" data-testid={`deduction-desc-${idx}`} />
                    <Input type="number" step="0.01" value={it.amount} onChange={(e) => setLine("deductions", idx, "amount", e.target.value)} className="w-32" data-testid={`deduction-amount-${idx}`} />
                    <Button type="button" variant="ghost" size="icon" className="text-rose-500" onClick={() => rmLine("deductions", idx)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>

              <div className="bg-[#F1F5F9] rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Gross</span><span className="font-medium">{money(gross)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total Deductions</span><span className="text-rose-600">−{money(Number(form.paye) + Number(form.uif) + otherDed)}</span></div>
                <div className="flex justify-between pt-1 border-t border-slate-200 font-bold text-[#0B132B]"><span>Net Pay</span><span data-testid="payslip-net">{money(net)}</span></div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-payslip-button">{saving ? "Saving…" : "Create Payslip"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
